<script setup>
import { computed } from 'vue';
import { NodeViewWrapper } from '@tiptap/vue-3';

const props = defineProps({
  node: { type: Object, required: true },
  deleteNode: { type: Function, required: true },
});

const hasError = computed(() => Boolean(props.node.attrs.error));
const isUploading = computed(() => Boolean(props.node.attrs.uploading));
const showPreview = computed(
  () => props.node.attrs.isImage && Boolean(props.node.attrs.preview)
);
</script>

<template>
  <NodeViewWrapper
    as="span"
    class="inline-flex align-middle mx-0.5"
    contenteditable="false"
  >
    <span
      class="group/chip inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-lg bg-n-solid-3 ring-1 text-xs max-w-full transition-colors"
      :class="
        hasError
          ? 'ring-n-ruby-7 hover:ring-n-ruby-8'
          : 'ring-n-weak hover:ring-n-brand/50'
      "
      :aria-label="node.attrs.name"
      :title="node.attrs.error || undefined"
    >
      <button
        type="button"
        class="relative size-3.5 shrink-0 rounded overflow-hidden"
        :aria-label="`Remove ${node.attrs.name}`"
        @click.stop="deleteNode()"
      >
        <img
          v-if="showPreview"
          :src="node.attrs.preview"
          :alt="node.attrs.name"
          class="absolute inset-0 size-full object-cover transition-opacity group-hover/chip:opacity-0"
        />
        <span
          v-else-if="isUploading"
          class="i-lucide-loader-2 absolute inset-0 text-n-slate-11 animate-spin"
        />
        <span
          v-else
          class="i-lucide-file absolute inset-0 transition-opacity group-hover/chip:opacity-0"
          :class="hasError ? 'text-n-ruby-9' : 'text-n-slate-11'"
        />
        <span
          class="i-lucide-x absolute inset-0 transition-opacity"
          :class="
            hasError
              ? 'text-n-ruby-9 opacity-100'
              : 'text-n-slate-11 opacity-0 group-hover/chip:opacity-100'
          "
        />
      </button>
      <span
        class="max-w-32 truncate"
        :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-12'"
      >
        {{ node.attrs.name }}
      </span>
      <span
        v-if="isUploading && showPreview"
        class="i-lucide-loader-2 size-3 text-n-slate-9 animate-spin shrink-0"
      />
    </span>
  </NodeViewWrapper>
</template>
